import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  QrCode,
  Users,
  MapPin,
  Table as TableIcon,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  delete_table_res,
  get_all_tables_res,
  server_post_data,
  toggle_show_hide_res,
} from "../ServiceConnection/serviceconnection";

export default function TableManagement() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [tables, setTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Dialog states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch tables
  const fetchTables = useCallback(async () => {
    setShowLoaderAdmin(true);
    setLoading(true);
    const fd = new FormData();
    fd.append("flag", 1);

    try {
      const response = await server_post_data(get_all_tables_res, fd);

      if (!response.data.error) {
        const tableData = response.data.message.data || [];
        setTables(tableData);
        setFilteredTables(tableData);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching tables:", error);
      toast({
        title: "Error",
        description: "Failed to load tables",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setLoading(false);
    }
  }, [toast]);

  // Handle search
  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);

      if (!query.trim()) {
        setFilteredTables(tables);
        return;
      }

      const searchLower = query.toLowerCase();
      const results = tables.filter((table) => {
        return (
          table.table_code?.toLowerCase().includes(searchLower) ||
          table.dining_area_name?.toLowerCase().includes(searchLower) ||
          table.max_person?.toString().toLowerCase().includes(searchLower)
        );
      });

      setFilteredTables(results);
    },
    [tables],
  );

  // Handle toggle status
  const handleToggleClick = useCallback((table) => {
    setSelectedTable(table);
    setIsToggleDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!selectedTable) return;

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("primary_id", selectedTable.primary_id);

    try {
      const response = await server_post_data(toggle_show_hide_res, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Table status updated successfully",
        });
        fetchTables();
      }
    } catch (error) {
      console.error("Toggle error:", error);
      toast({
        title: "Error",
        description: "Failed to update table status",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsToggleDialogOpen(false);
      setSelectedTable(null);
    }
  }, [selectedTable, toast, fetchTables]);

  // Handle delete
  const handleDeleteClick = useCallback((table) => {
    setSelectedTable(table);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedTable) return;

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("primary_id", selectedTable.primary_id);

    try {
      const response = await server_post_data(delete_table_res, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Table deleted successfully",
        });
        fetchTables();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description: "Failed to delete table",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsDeleteDialogOpen(false);
      setSelectedTable(null);
    }
  }, [selectedTable, toast, fetchTables]);

  // Handle QR code
  const handleQrCode = useCallback(
    (table) => {
      navigate("/QrGenrate", {
        state: {
          TableId: table.primary_id,
          TableName: table.table_code,
        },
      });
    },
    [navigate],
  );

  // Initial load
  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // Calculate stats
  const stats = {
    total: tables.length,
    active: tables.filter((t) => t.show_hide === 1).length,
    inactive: tables.filter((t) => t.show_hide === 0).length,
  };

  return (
    <MainLayout>
      <Header
        title="Table Management"
        subtitle="Manage restaurant tables and seating arrangements"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <TableIcon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.total}
                </p>
                <p className="text-sm text-muted-foreground">Total Tables</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <TableIcon className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.active}
                </p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <TableIcon className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.inactive}
                </p>
                <p className="text-sm text-muted-foreground">Inactive</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by table name, area, or capacity..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  setFilteredTables(tables);
                }}
              >
                Clear
              </Button>
            )}
          </div>
          <Button
            onClick={() => navigate("/add-table")}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Add New Table
          </Button>
        </div>

        {/* Tables List */}
        <Card>
          <CardContent className="p-0">
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground w-[80px]">
                      Sr.No
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Table Name
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Capacity
                      </div>
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Area
                      </div>
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      Status
                    </TableHead>
                    <TableHead className="text-muted-foreground text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-12 text-muted-foreground"
                      >
                        Loading Tables...
                      </TableCell>
                    </TableRow>
                  ) : filteredTables.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-muted-foreground"
                      >
                        No tables found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTables.map((table, index) => (
                      <TableRow
                        key={table.primary_id}
                        className="border-border"
                      >
                        <TableCell className="font-medium">
                          {index + 1}
                        </TableCell>
                        <TableCell className="font-semibold">
                          {table.table_code}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            <Users className="w-3 h-3" />
                            {table.max_person} persons
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            {table.dining_area_name || "No Area"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={table.show_hide === 1}
                            onCheckedChange={() => handleToggleClick(table)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleQrCode(table)}
                              className="hover:bg-primary/10 hover:text-primary"
                              title="Generate QR Code"
                            >
                              <QrCode className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate("/edit-table", {
                                  state: { primary_id: table.primary_id },
                                })
                              }
                              className="hover:bg-primary/10 hover:text-primary"
                              title="Edit Table"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(table)}
                              className="hover:bg-destructive/10 hover:text-destructive"
                              title="Delete Table"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Table</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete table{" "}
                <strong>{selectedTable?.table_code}</strong>? This action cannot
                be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="bg-destructive hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Toggle Status Confirmation Dialog */}
        <AlertDialog
          open={isToggleDialogOpen}
          onOpenChange={setIsToggleDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Table Status</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to{" "}
                {selectedTable?.show_hide === 1 ? "deactivate" : "activate"}{" "}
                table <strong>{selectedTable?.table_code}</strong>?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmToggle}>
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
